export const COMPETITOR_MAP_MIN_ZOOM = 5;
export const COMPETITOR_MAP_MAX_ZOOM = 18;
/** Fallback when only “you” is on the board (~4–5 km). */
export const COMPETITOR_MAP_DEFAULT_ALONE_ZOOM = 11;
/** Cap auto-fit so a tiny cluster doesn’t snap to street level. */
export const COMPETITOR_MAP_MAX_FIT_ZOOM = 14;
export const COMPETITOR_MAP_MIN_SIZE_PX = 80;

export type CompetitorMapFitPadding = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export function competitorMapCanFit(width: number, height: number): boolean {
  return (
    width >= COMPETITOR_MAP_MIN_SIZE_PX && height >= COMPETITOR_MAP_MIN_SIZE_PX
  );
}

/**
 * Pixel padding for Google Maps `fitBounds`. Desktop can spare ~90px so
 * chips stay inside the frame; a phone-width stage cannot — 96+112px of
 * pad on a 360px map left too little viewport, so auto-fit clamped at
 * zoom 8 and far tracked pins sat off-screen (DEV-435).
 */
export function competitorMapFitPaddingPx(viewport: {
  width: number;
  height: number;
}): CompetitorMapFitPadding {
  const cap = (desired: number, dim: number) => {
    const max = Math.max(16, Math.floor(dim * 0.16));
    return Math.min(desired, max);
  };
  return {
    top: cap(72, viewport.height),
    right: cap(88, viewport.width),
    bottom: cap(88, viewport.height),
    left: cap(72, viewport.width),
  };
}

/**
 * Extra right/bottom inset for chrome that covers the map (detail sheet,
 * legend). Overlay width may exceed the 16% fit cap — that’s intentional
 * so pins sit in the remaining optical frame, not under the sheet.
 */
export function competitorMapOpticalPadding(input: {
  width: number;
  height: number;
  overlayRightPx?: number;
  overlayBottomPx?: number;
}): CompetitorMapFitPadding {
  const base = competitorMapFitPaddingPx({
    width: input.width,
    height: input.height,
  });
  const maxRight = Math.max(base.right, Math.floor(input.width * 0.42));
  const maxBottom = Math.max(base.bottom, Math.floor(input.height * 0.28));
  const right = Math.min(
    maxRight,
    Math.max(base.right, input.overlayRightPx ?? 0),
  );
  const bottom = Math.min(
    maxBottom,
    Math.max(base.bottom, input.overlayBottomPx ?? 0),
  );
  return { ...base, right, bottom };
}

/** Pixels to pan so a point at the map’s geometric center lands in the optical center. */
export function opticalPanPixels(padding: CompetitorMapFitPadding): {
  x: number;
  y: number;
} {
  return {
    x: (padding.right - padding.left) / 2,
    y: (padding.bottom - padding.top) / 2,
  };
}

export function clampCompetitorFitZoom(zoom: number): number {
  if (zoom > COMPETITOR_MAP_MAX_FIT_ZOOM) {
    return COMPETITOR_MAP_MAX_FIT_ZOOM;
  }
  if (zoom < COMPETITOR_MAP_MIN_ZOOM) {
    return COMPETITOR_MAP_MIN_ZOOM;
  }
  return zoom;
}

export function competitorMapSizeJumped(
  prev: { width: number; height: number },
  next: { width: number; height: number },
): boolean {
  return (
    Math.abs(next.width - prev.width) >= 48 ||
    Math.abs(next.height - prev.height) >= 48
  );
}
